import { BaseNumber, bigNumberify, NormalizedNumber, Percentage, promiseAll } from '@sparkdotfi/common-universal'
import { QueryKey, queryOptions } from '@tanstack/react-query'
import { zeroAddress } from 'viem'
import { Config } from 'wagmi'
import { readContract, simulateContract } from 'wagmi/actions'

import { jugConfig, lockstakeEngineConfig, stusdsAbi, stusdsAddress, vatConfig } from '@/config/contracts-generated'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { fromRay, fromWad } from '@/utils/math'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function stusdsWithdrawDataQueryOptions({ wagmiConfig, chainId }: { wagmiConfig: Config; chainId: number }) {
  return queryOptions({
    queryKey: stusdsWithdrawDataQueryKey({ chainId }),
    queryFn: () => stusdsWithdrawDataQueryFunction({ wagmiConfig, chainId }),
  })
}

export function stusdsWithdrawDataQueryKey({ chainId }: { chainId: number }): QueryKey {
  return ['stusds-withdraw-data', chainId]
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export async function stusdsWithdrawDataQueryFunction({
  wagmiConfig,
  chainId,
}: {
  wagmiConfig: Config
  chainId: number
}) {
  const skyIlk = await readContract(wagmiConfig, {
    address: getContractAddress(lockstakeEngineConfig.address, chainId),
    abi: lockstakeEngineConfig.abi,
    functionName: 'ilk',
    chainId,
  })

  const { totalSupply, totalAssets, jugData, vatData } = await promiseAll(
    {
      totalSupply: () =>
        readContract(wagmiConfig, {
          address: getContractAddress(stusdsAddress, chainId),
          abi: stusdsAbi,
          functionName: 'totalSupply',
          chainId,
        }),
      totalAssets: () =>
        readContract(wagmiConfig, {
          address: getContractAddress(stusdsAddress, chainId),
          abi: stusdsAbi,
          functionName: 'totalAssets',
          chainId,
        }),
      jugData: () =>
        promiseAll(
          {
            ilk: () =>
              readContract(wagmiConfig, {
                address: getContractAddress(jugConfig.address, chainId),
                abi: jugConfig.abi,
                functionName: 'ilks',
                args: [skyIlk],
                chainId,
              }),
            newRate: async () => {
              const { result } = await simulateContract(wagmiConfig, {
                address: getContractAddress(jugConfig.address, chainId),
                abi: jugConfig.abi,
                functionName: 'drip',
                args: [skyIlk],
                account: zeroAddress,
                chainId,
              })

              return result
            },
          },
          { concurrency: 'unbounded' },
        ),
      vatData: () =>
        readContract(wagmiConfig, {
          address: getContractAddress(vatConfig.address, chainId),
          abi: vatConfig.abi,
          functionName: 'ilks',
          args: [skyIlk],
          chainId,
        }),
    },
    { concurrency: 'unbounded' },
  )

  const Art = fromWad(bigNumberify(vatData[0]))
  const rate = fromRay(bigNumberify(jugData.newRate))

  return {
    totalSupply: BaseNumber(totalSupply).toNormalizedUnit(18),
    totalAssets: BaseNumber(totalAssets).toNormalizedUnit(18),
    lockstakeDebt: NormalizedNumber(Art.times(rate)),
    lockstakeDebtRate: Percentage(fromRay(bigNumberify(jugData.ilk[0])), { allowMoreThan1: true }),
  }
}
